import { useMemo } from 'react'

import type {
  TemplateVariableGroup,
  TemplateVariableItem,
  TemplateVariableSubgroup,
} from '#/shared/api/generated/shipment/models'
import {
  useGetTemplateContextApiV1TemplatesShipmentIdTemplateContextGet,
  useListTemplateVariablesApiV1TemplatesVariablesGet,
} from '#/shared/api/generated/shipment/theCargoShipment'
import { relabelTemplateChips } from '#/shared/lib/html'

export interface InsertFieldOption {
  id: string
  label: string
  suffix?: string
  insert: string
  /** No value resolved for this shipment — selectable only where the
   *  backend renders per recipient (mass jobs), not on direct sends. */
  disabled?: boolean
}

export interface InsertFieldGroup {
  label: string
  items: InsertFieldOption[]
  subgroups: InsertFieldGroup[]
}

export interface UseTemplateVariableOptionsResult {
  options: string[]
  groups: InsertFieldGroup[]
  /** Insert token → value resolved for the shipment (untruncated). Only
   *  populated when a `shipmentId` was given; tokens without a resolvable
   *  value are absent. */
  insertValues: Record<string, string>
  isLoading: boolean
}

const MAX_SUFFIX_LENGTH = 60

function formatValue(value: unknown): string | undefined {
  if (value === null || value === undefined || value === '') return undefined
  if (typeof value === 'string') return truncate(value)
  if (typeof value === 'number' || typeof value === 'boolean') {
    return truncate(String(value))
  }
  return undefined
}

function truncate(text: string): string {
  const collapsed = text.replace(/\s+/g, ' ').trim()
  if (collapsed.length <= MAX_SUFFIX_LENGTH) return collapsed
  return `${collapsed.slice(0, MAX_SUFFIX_LENGTH - 1)}…`
}

function toOption(
  item: TemplateVariableItem,
  withValues: boolean,
): InsertFieldOption {
  const value = formatValue(item.value)
  return {
    id: item.path,
    label: item.label,
    suffix: value ?? item.path,
    insert: item.insert ?? `{{${item.path}}}`,
    // Only a shipment context resolves values; without one (mass jobs)
    // every field stays selectable and the backend renders per recipient.
    disabled: withValues && value === undefined,
  }
}

function toGroup(
  group: TemplateVariableGroup | TemplateVariableSubgroup,
  withValues: boolean,
): InsertFieldGroup {
  const subgroups =
    'subgroups' in group
      ? group.subgroups.map((sub) => toGroup(sub, withValues))
      : []
  return {
    label: group.label,
    items: group.items.map((item) => toOption(item, withValues)),
    subgroups,
  }
}

/** Full replacement text for a chip — unlike the menu hint, never truncated. */
function toInsertValue(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return undefined
}

function collectInsertValues(
  group: TemplateVariableGroup | TemplateVariableSubgroup,
  acc: Record<string, string>,
): void {
  group.items.forEach((item) => {
    const resolved = toInsertValue(item.value)
    if (resolved !== undefined) {
      acc[item.insert ?? `{{${item.path}}}`] = resolved
    }
  })
  if ('subgroups' in group) {
    group.subgroups.forEach((subgroup) => collectInsertValues(subgroup, acc))
  }
}

function collectGroupLabels(group: TemplateVariableGroup): string[] {
  const direct = group.items.map((item: TemplateVariableItem) => item.label)
  const nested = group.subgroups.flatMap((subgroup) =>
    subgroup.items.map((item) => item.label),
  )
  return [...direct, ...nested]
}

function dedupe(values: string[]): string[] {
  return Array.from(new Set(values.filter((label) => label.length > 0)))
}

/** Relabel chips in a template body: any chip whose saved display text is the
 *  raw token (e.g. the vehicles Jinja loop) gets its friendly field label. */
export function relabelTemplateBody(
  body: string,
  groups: InsertFieldGroup[],
): string {
  return relabelTemplateChips(body, insertFieldLabelMap(groups))
}

/** Insert token → friendly label across the whole field tree — for relabeling
 *  chips in saved template bodies whose display text is the raw token (e.g.
 *  the vehicles Jinja loop). */
function insertFieldLabelMap(groups: InsertFieldGroup[]): Map<string, string> {
  const map = new Map<string, string>()
  const walk = (group: InsertFieldGroup) => {
    group.items.forEach((item) => map.set(item.insert, item.label))
    group.subgroups.forEach(walk)
  }
  groups.forEach(walk)
  return map
}

export function useTemplateVariableOptions(
  shipmentId?: string,
): UseTemplateVariableOptionsResult {
  const variablesQuery = useListTemplateVariablesApiV1TemplatesVariablesGet({
    query: { enabled: !shipmentId },
  })
  const contextQuery =
    useGetTemplateContextApiV1TemplatesShipmentIdTemplateContextGet(
      shipmentId ?? '',
      { query: { enabled: Boolean(shipmentId) } },
    )
  const activeQuery = shipmentId ? contextQuery : variablesQuery
  const apiGroups = activeQuery.data?.data.groups ?? []

  const options = useMemo(
    () => dedupe(apiGroups.flatMap(collectGroupLabels)),
    [apiGroups],
  )
  const groups = useMemo(
    () => apiGroups.map((group) => toGroup(group, Boolean(shipmentId))),
    [apiGroups, shipmentId],
  )
  const insertValues = useMemo(() => {
    const acc: Record<string, string> = {}
    apiGroups.forEach((group) => collectInsertValues(group, acc))
    return acc
  }, [apiGroups])

  return {
    options,
    groups,
    insertValues,
    isLoading: activeQuery.isLoading || activeQuery.isFetching,
  }
}
